// No React hooks needed in this showcase
import { Skeleton } from '@components/base/feedback';
import { Text } from '@components/base/display';
import { VStack, HStack, Card, CardContent } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type SkeletonVariant = 'text' | 'circular' | 'rectangular';

function getSkeletonHeight(variant: string | undefined): number | undefined {
  if (variant === 'circular') {
    return 48;
  }
  if (variant === 'rectangular') {
    return 100;
  }
  return undefined;
}

function SkeletonShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="skeleton">
          <div className="flex items-center gap-4">
            <Skeleton
              variant={playgroundProps.variant as SkeletonVariant}
              animation={playgroundProps.animation as boolean}
              width={playgroundProps.variant === 'circular' ? 48 : 200}
              height={getSkeletonHeight(playgroundProps.variant as string)}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Shape Variants */}
      <ShowcaseDemoSection
        title="Shape Variants"
        description="Three shape variants for different content types"
       
      >
          <VStack gap={6}>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Text (rounded-sm)</Text>
              <Skeleton variant="text" width="80%" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Circular (rounded-full)</Text>
              <Skeleton variant="circular" width={48} height={48} />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Rectangular (rounded-md)</Text>
              <Skeleton variant="rectangular" width="100%" height={120} />
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Animation Toggle */}
      <ShowcaseDemoSection
        title="Animation Toggle"
        description="Enable or disable the shimmer animation"
       
      >
          <HStack gap={8}>
            <VStack gap={2} className="flex-1">
              <Text size="sm" weight="medium">With Animation</Text>
              <Skeleton variant="rectangular" height={60} animation />
            </VStack>
            <VStack gap={2} className="flex-1">
              <Text size="sm" weight="medium">Without Animation</Text>
              <Skeleton variant="rectangular" height={60} animation={false} />
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Text Skeletons */}
      <ShowcaseDemoSection
        title="Text Skeletons"
        description="Simulate paragraph text with multiple skeleton lines"
       
      >
          <VStack gap={4}>
            {/* Single line */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">Heading</Text>
              <Skeleton variant="text" width="40%" height="1.5rem" />
            </VStack>

            {/* Paragraph */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">Paragraph</Text>
              <VStack gap={2}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="75%" />
              </VStack>
            </VStack>

            {/* Mixed content */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">Mixed Content</Text>
              <Skeleton variant="text" width="50%" height="1.5rem" />
              <VStack gap={2} className="mt-2">
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="90%" />
              </VStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Card Skeleton */}
      <ShowcaseDemoSection
        title="Card Skeleton"
        description="Compose skeletons to represent a loading card"
       
      >
          <HStack gap={4} wrap="wrap">
            {/* Policy card skeleton */}
            <Card className="w-72">
              <CardContent>
                <VStack gap={4}>
                  <Skeleton variant="rectangular" height={140} />
                  <VStack gap={2}>
                    <Skeleton variant="text" width="60%" height="1.25rem" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="40%" />
                  </VStack>
                  <HStack gap={2}>
                    <Skeleton variant="rectangular" width={80} height={32} />
                    <Skeleton variant="rectangular" width={80} height={32} />
                  </HStack>
                </VStack>
              </CardContent>
            </Card>

            {/* Profile card skeleton */}
            <Card className="w-72">
              <CardContent>
                <VStack gap={4}>
                  <HStack gap={3}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <VStack gap={2} className="flex-1">
                      <Skeleton variant="text" width="70%" height="1.25rem" />
                      <Skeleton variant="text" width="50%" />
                    </VStack>
                  </HStack>
                  <VStack gap={2}>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="60%" />
                  </VStack>
                </VStack>
              </CardContent>
            </Card>
          </HStack>
      </ShowcaseDemoSection>

      {/* List Skeleton */}
      <ShowcaseDemoSection
        title="List Skeleton"
        description="Simulate a loading list of items"
       
      >
          <VStack gap={0}>
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="flex items-center gap-4 py-4 border-b border-neutral-200 last:border-b-0"
              >
                <Skeleton variant="circular" width={40} height={40} />
                <VStack gap={2} className="flex-1">
                  <HStack justify="between" className="w-full">
                    <Skeleton variant="text" width="40%" height="1rem" />
                    <Skeleton variant="text" width="20%" height="0.875rem" />
                  </HStack>
                  <Skeleton variant="text" width="70%" />
                </VStack>
              </div>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Real-world Example: Claims Dashboard */}
      <ShowcaseDemoSection
        title="Real-world Example"
        description="Claims dashboard loading state"
       
      >
          <VStack gap={6}>
            {/* Stats row */}
            <HStack gap={4} wrap="wrap">
              {[1, 2, 3, 4].map((stat) => (
                <Card key={stat} className="flex-1 min-w-[140px]">
                  <CardContent className="py-4">
                    <VStack gap={2}>
                      <Skeleton variant="text" width="50%" height="0.875rem" />
                      <Skeleton variant="text" width="70%" height="2rem" />
                    </VStack>
                  </CardContent>
                </Card>
              ))}
            </HStack>

            {/* Claims table skeleton */}
            <Card>
              <CardContent>
                <VStack gap={4}>
                  {/* Table header */}
                  <HStack gap={4} className="pb-2 border-b border-neutral-200">
                    <Skeleton variant="text" width="15%" />
                    <Skeleton variant="text" width="25%" />
                    <Skeleton variant="text" width="20%" />
                    <Skeleton variant="text" width="15%" />
                    <Skeleton variant="text" width="15%" />
                  </HStack>
                  {/* Table rows */}
                  {[1, 2, 3, 4, 5].map((row) => (
                    <HStack key={row} gap={4} className="py-2">
                      <Skeleton variant="text" width="15%" />
                      <Skeleton variant="text" width="25%" />
                      <Skeleton variant="text" width="20%" />
                      <Skeleton variant="text" width="15%" />
                      <Skeleton variant="rectangular" width="15%" height={24} />
                    </HStack>
                  ))}
                </VStack>
              </CardContent>
            </Card>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const SkeletonShowcase: ShowcaseDefinition = {
  id: 'skeleton',
  name: 'Skeleton',
  category: 'feedback',
  description: 'Loading placeholder that mimics content shape',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['text', 'circular', 'rectangular'],
      defaultValue: 'text',
      description: 'Shape of the skeleton placeholder',
    },
    {
      name: 'animation',
      type: 'boolean',
      defaultValue: true,
      description: 'Whether to show the shimmer animation',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-hidden="true"',
    ],
    keyboardNav: [],
    notes: [
      'Skeletons are decorative and hidden from screen readers',
      'Container should have aria-busy="true" during loading',
      'Respects prefers-reduced-motion preference',
      'Provide actual content structure for screen readers when loading',
    ],
  },
  component: SkeletonShowcaseComponent,
};
